/* ============================================
   TFS Wheels — Global JS
   PubSub, Cart API, Utilities
   ============================================ */

/* --- PubSub Event System --- */
const PubSub = (() => {
  const events = {};
  return {
    subscribe(event, callback) {
      events[event] = events[event] || [];
      events[event].push(callback);
      return () => {
        events[event] = events[event].filter(cb => cb !== callback);
      };
    },
    publish(event, data) {
      if (events[event]) {
        events[event].forEach(callback => callback(data));
      }
    }
  };
})();

/* --- Cart API Helpers --- */
const CartAPI = {
  async get() {
    const res = await fetch(window.routes.cart_url + '.js');
    return res.json();
  },

  async add(items) {
    const before = currentCartCount();
    const res = await fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    const data = await res.json();
    if (!res.ok) {
      /* A 422 can still be a PARTIAL add ("Only 2 items were added to your
         cart due to availability.") — Shopify keeps what fit. Re-read the
         cart so the badge + drawer show what actually landed instead of
         staying stale until a page refresh. */
      const err = new Error(data.description || data.message);
      try {
        const cart = await CartAPI.get();
        err.partial = cart.item_count > before;
        if (err.partial) PubSub.publish('cart:updated', cart);
        else updateCartCount(cart.item_count);
      } catch (e) { /* keep the original error */ }
      throw err;
    }
    PubSub.publish('cart:updated');
    return data;
  },

  async change(body) {
    const res = await fetch(window.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message);
    PubSub.publish('cart:updated');
    return data;
  },

  async update(body) {
    const res = await fetch(window.routes.cart_update_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message);
    PubSub.publish('cart:updated');
    return data;
  }
};

/* --- Floating toast ---
   One fixed notice under the header for cart/add-to-cart feedback, so it
   never pushes or overlaps the buy box. TFSToast.show(msg, { tone, ms }). */
const TFSToast = (() => {
  let el, timer;
  function topOffset() {
    const hdr = document.querySelector('sticky-header, .header-wrapper');
    const bottom = hdr ? hdr.getBoundingClientRect().bottom : 0;
    return Math.max(12, Math.round(bottom) + 12);
  }
  function hide() {
    if (!el) return;
    clearTimeout(timer);
    el.classList.remove('is-open');
  }
  function show(message, opts = {}) {
    if (!el) {
      el = document.createElement('div');
      el.className = 'tfs-toast';
      el.setAttribute('role', 'alert');
      el.innerHTML = '<span class="tfs-toast__msg"></span>' +
        '<button type="button" class="tfs-toast__close" aria-label="Dismiss">&times;</button>';
      el.querySelector('.tfs-toast__close').addEventListener('click', hide);
      document.body.appendChild(el);
    }
    el.dataset.tone = opts.tone || 'error';
    el.querySelector('.tfs-toast__msg').textContent = message;
    el.style.top = topOffset() + 'px';
    el.classList.remove('is-open');
    void el.offsetWidth; // restart the enter animation on repeat messages
    el.classList.add('is-open');
    clearTimeout(timer);
    timer = setTimeout(hide, opts.ms || 6000);
  }
  return { show, hide };
})();
window.TFSToast = TFSToast;

/* --- Product carousels: prev/next arrows (featured-collection) ---
   The track hides its scrollbar, so desktop mouse users need buttons. Each
   arrow is hidden when there is nothing further in its direction. */
function initCarousels(root = document) {
  root.querySelectorAll('[data-carousel]').forEach(wrap => {
    if (wrap.dataset.carouselReady) return;
    wrap.dataset.carouselReady = '1';
    const track = wrap.querySelector('[data-carousel-track]');
    const prev = wrap.querySelector('[data-carousel-dir="-1"]');
    const next = wrap.querySelector('[data-carousel-dir="1"]');
    if (!track || !prev || !next) return;
    const sync = () => {
      const max = track.scrollWidth - track.clientWidth;
      prev.hidden = track.scrollLeft <= 2;
      next.hidden = max <= 2 || track.scrollLeft >= max - 2;
    };
    wrap.addEventListener('click', e => {
      const btn = e.target.closest('[data-carousel-dir]');
      if (!btn) return;
      // One "page" = visible width + the flex gap, so 4 cards advance exactly.
      const page = track.clientWidth + (parseFloat(getComputedStyle(track).columnGap) || 0);
      track.scrollBy({ left: page * Number(btn.dataset.carouselDir), behavior: 'smooth' });
    });
    track.addEventListener('scroll', debounce(sync, 60), { passive: true });
    window.addEventListener('resize', debounce(sync, 150));
    sync();
  });
}
document.addEventListener('DOMContentLoaded', () => initCarousels());
document.addEventListener('shopify:section:load', e => initCarousels(e.target));

/* --- Debounce --- */
function debounce(fn, delay = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/* --- Trap Focus --- */
function trapFocus(container, elementToFocus = container) {
  const focusable = container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  function handleKeydown(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  container.addEventListener('keydown', handleKeydown);
  if (elementToFocus) elementToFocus.focus();

  return () => container.removeEventListener('keydown', handleKeydown);
}

/* --- Remove Trap Focus --- */
function removeTrapFocus(container) {
  // Focus returns to previously focused element
  if (container && container._previousFocus) {
    container._previousFocus.focus();
  }
}

/* --- Format Money --- */
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = (cents / 100).toFixed(2);
  const moneyFormat = format || window.Shopify?.money_format || '${{amount}}';
  return moneyFormat
    .replace('{{amount}}', value)
    .replace('{{amount_no_decimals}}', Math.round(cents / 100))
    .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
}

/* --- Fetch Section HTML --- */
async function fetchSection(sectionId, url) {
  const fetchUrl = url || window.location.pathname;
  const separator = fetchUrl.includes('?') ? '&' : '?';
  const res = await fetch(`${fetchUrl}${separator}sections=${sectionId}`);
  const data = await res.json();
  return data[sectionId];
}

/* --- Cart Count Badge --- */
function currentCartCount() {
  const el = document.querySelector('.cart-count-badge');
  return (el && !el.hidden && parseInt(el.textContent, 10)) || 0;
}

function updateCartCount(count) {
  document.querySelectorAll('.cart-count-badge').forEach(el => {
    el.textContent = count;
    el.toggleAttribute('hidden', count === 0);
  });
}

// Listen for cart updates to refresh count
PubSub.subscribe('cart:updated', async () => {
  try {
    const cart = await CartAPI.get();
    updateCartCount(cart.item_count);
  } catch (e) {
    // Silently fail
  }
});

/* --- Quantity Input Web Component --- */
if (!customElements.get('quantity-input')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.minus = this.querySelector('[name="minus"]');
      this.plus = this.querySelector('[name="plus"]');
    }

    connectedCallback() {
      this.minus?.addEventListener('click', this.decrement.bind(this));
      this.plus?.addEventListener('click', this.increment.bind(this));
      this.input?.addEventListener('change', this.validate.bind(this));
    }

    decrement() {
      // min="0" (cart line — decrement-to-remove) is falsy, so `|| 1` wrongly forced min=1
      const min = Number.isNaN(parseInt(this.input.min)) ? 1 : parseInt(this.input.min);
      const value = parseInt(this.input.value) - 1;
      this.input.value = Math.max(min, value);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    increment() {
      const max = parseInt(this.input.max) || 999;
      const value = parseInt(this.input.value) + 1;
      this.input.value = Math.min(max, value);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    validate() {
      // min="0" (cart line — decrement-to-remove) is falsy, so `|| 1` wrongly forced min=1
      const min = Number.isNaN(parseInt(this.input.min)) ? 1 : parseInt(this.input.min);
      const max = parseInt(this.input.max) || 999;
      let value = parseInt(this.input.value);
      if (isNaN(value) || value < min) value = min;
      if (value > max) value = max;
      this.input.value = value;
    }
  }

  customElements.define('quantity-input', QuantityInput);
}
